import { Box, DialogContent, Divider, FormControlLabel, IconButton, Radio, Typography } from '@mui/material'
import { Add, Close, Search } from '@mui/icons-material'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import { FormikProvider, useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'

import { LocationAddressOptionType } from 'views/Authorized/views/Locations/types'
import { findCountryKeyByName } from 'shared/utils/helpers/findCountryName'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import { CustomerType } from 'shared/store/useCustomers/types'
import { DeliveryTypes, PaymentMethods } from 'shared/consts'
import getCompanyData from 'shared/services/getCompanyData'
import useCountriesQuery from 'shared/store/useCountries'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'
import MuiPhone from 'shared/components/PhoneInput'
import validatePhoneNumber from 'shared/utils/helpers/validatePhoneNumber'

import { schema } from '../../../../schema'

interface Props {
   onSubmit: (customerDetails: CustomerType) => void
   onClose: () => void
   isSubmitting: boolean
   customerDetails?: CustomerType
}

export default function NewModal({ onSubmit, customerDetails, isSubmitting, onClose }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const { data: countriesData } = useCountriesQuery()
   const { addNotification } = useNotificationContext()
   const [isCompanyDataFetching, setIsCompanyDataFetching] = useState(false)
   const [isCompany, setIsCompany] = useState(true)
   const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false)

   const [options, setOptions] = useState<LocationAddressOptionType>({
      voivodeshipOptions: [],
      countyOptions: [],
   })

   const formik = useFormik<CustomerType>({
      initialValues: {
         name: '',
         surname: '',
         uuid: '',
         displayName: '',
         companyName: '',
         contacts: [],
         taxID: '',
         deliveryAddresses: [],
         deliveryType: '',
         paymentMethod: '',
         address: {
            street: '',
            houseNumber: '',
            zipCode: '',
            city: '',
            country: 'Polska',
            voivodeship: '',
            county: '',
         },
         productsMarkupSettings: {
            priceType: 'GROSS',
            thresholds: [],
         },
      },
      onSubmit: () => {
         if ((!!values.name && !!values.surname) || (!!values.taxID && !!values.companyName)) {
            onSubmit({
               ...values,
               name: values.name ? values.name : null,
               surname: values.surname ? values.surname : null,
               companyName: values.companyName ? values.companyName : null,
               taxID: values.taxID ? values.taxID : null,
               paymentMethod: values.paymentMethod ? values.paymentMethod : null,
               deliveryType: values.deliveryType ? values.deliveryType : null,
            })
         } else {
            addNotification(t('CUSTOMERS:noCompanyOrCustomerDetailsError'), 'error')
         }
      },
      validationSchema: schema,
   })

   const { values, setFieldValue, setValues, validateForm, setTouched, setFieldError, handleSubmit, setErrors } = formik

   function touchAllFields(obj: any): any {
      if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
         return Object.keys(obj).reduce(
            (acc, key) => {
               acc[key] = touchAllFields(obj[key])
               return acc
            },
            {} as { [key: string]: boolean | typeof obj }
         )
      }

      return true
   }

   function handlePhoneNumber(value: string, country: string, index: number) {
      setFieldValue(`contacts[${index}].phoneNumber`, value)
      const result = validatePhoneNumber(value, country)
      if (result === false) {
         setPhoneNumberError(true)
      } else {
         setPhoneNumberError(false)
      }
   }

   async function handleOnSearch() {
      setIsCompanyDataFetching(true)
      await getCompanyData(values.taxID!)
         .then((res) => {
            if (res) {
               setValues(
                  {
                     ...values,
                     companyName: res.companyName,
                     displayName: res.companyName,
                     address: {
                        country: 'Polska',
                        voivodeship: res.voivodeship ?? values.address.voivodeship,
                        houseNumber: res.houseNumber ?? values.address.houseNumber,
                        county: res.county ?? values.address.county,
                        city: res.city ?? values.address.city,
                        street: res.street ?? values.address.street,
                        zipCode: res.postalCode ?? values.address.zipCode,
                     },
                  },
                  true
               )
            }
            validateForm({
               ...values,
               displayName: res.companyName,
               companyName: res.companyName,
               address: {
                  country: 'Polska',
                  voivodeship: res.voivodeship,
                  houseNumber: res.houseNumber,
                  county: res.county,
                  city: res.city,
                  street: res.street,
                  zipCode: res.postalCode,
               },
            }).then((errors) => {
               const allFieldsTouched = touchAllFields(values)
               setTouched(allFieldsTouched).then(() => {
                  setErrors(errors)
               })
            })
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               setFieldError('taxID', t(`TRANSLATION:${error.response?.data?.errors[0].code}`))
            } else {
               setFieldError('taxID', error)
            }
         })
         .finally(() => setIsCompanyDataFetching(false))
   }

   useEffect(() => {
      if (countriesData && formik.values && values && formik.values.address.country === 'Polska') {
         const countryKey = findCountryKeyByName(countriesData, formik.values.address.country)
         if (countryKey) {
            const selectedCountry = countriesData[countryKey]
            if (selectedCountry) {
               setOptions({
                  voivodeshipOptions: Object.keys(selectedCountry.voivodeships).map((item) => ({
                     name: item,
                     value: item,
                  })),
                  countyOptions: formik.values.address.voivodeship
                     ? selectedCountry.voivodeships[formik.values.address.voivodeship].map((item) => ({
                          name: item,
                          value: item,
                       }))
                     : [],
               })
            }
         }
      }
   }, [countriesData, formik.values, values])

   useEffect(() => {
      if (customerDetails) {
         setValues({ ...customerDetails })
      }
   }, [customerDetails])

   useEffect(() => {
      if (!formik.touched.displayName && (!!values.name || !!values.surname)) {
         setFieldValue('displayName', `${values.name} ${values.surname}`)
      }
   }, [values.name, values.surname])

   return (
      <Box>
         <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="h4">{t('CUSTOMERS:modal.new.title')}</Typography>
               <IconButton onClick={onClose}>
                  <Close />
               </IconButton>
            </Box>
         </DialogTitle>
         <DialogContent>
            <FormikProvider value={formik}>
               <Box
                  sx={{
                     display: 'flex',
                     py: '8px',
                     flexDirection: 'column',
                     minWidth: { lg: '750px', md: '500px', xs: 'auto' },
                     gap: '8px',
                  }}
               >
                  <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                     <FormControlLabel
                        onClick={() => setIsCompany(true)}
                        checked={isCompany}
                        control={<Radio />}
                        label={t('CUSTOMERS:modal.new.company')}
                     />
                     <FormControlLabel
                        onClick={() => setIsCompany(false)}
                        checked={!isCompany}
                        control={<Radio />}
                        label={t('CUSTOMERS:modal.new.person')}
                     />
                  </Box>
                  {isCompany ? (
                     <>
                        <Typography variant="h6">{t('CUSTOMERS:modal.new.companyData')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <InputField
                              sx={{ flex: 0.33 }}
                              slotProps={{
                                 input: {
                                    endAdornment: (
                                       <ContainedButton
                                          disabled={isCompanyDataFetching || !values.taxID}
                                          onClick={handleOnSearch}
                                          variant="contained"
                                       >
                                          <Search />
                                       </ContainedButton>
                                    ),
                                 },
                              }}
                              name="taxID"
                              fullWidth
                              label={t('TRANSLATION:formFields.taxID')}
                           />

                           <InputField
                              sx={{ flex: 0.67 }}
                              name="companyName"
                              fullWidth
                              label={t('TRANSLATION:formFields.companyName')}
                           />
                        </Box>
                        <InputField fullWidth name="displayName" label="Nazwa" />
                     </>
                  ) : (
                     <>
                        <Typography variant="h6">{t('CUSTOMERS:modal.new.clientData')}</Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <InputField name="name" fullWidth label={t('TRANSLATION:formFields.name')} />

                           <InputField name="surname" fullWidth label={t('TRANSLATION:formFields.surname')} />
                        </Box>
                        <InputField fullWidth name="displayName" label="Nazwa" />
                     </>
                  )}
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                     <SelectField
                        searcher
                        noMinWidth
                        inputVariant="outlined"
                        fullWidth
                        name="address.voivodeship"
                        label={t('TRANSLATION:formFields.voivodeship')}
                        options={options.voivodeshipOptions}
                     />
                     <SelectField
                        searcher
                        fullWidth
                        noMinWidth
                        inputVariant="outlined"
                        name="address.county"
                        disabled={!values.address.voivodeship}
                        label={t('TRANSLATION:formFields.county')}
                        options={options.countyOptions}
                     />
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                     <InputField name="address.zipCode" fullWidth label={t('TRANSLATION:formFields.postCode')} />
                     <InputField name="address.city" fullWidth label={t('TRANSLATION:formFields.city')} />
                     <InputField name="address.street" fullWidth label={t('TRANSLATION:formFields.street')} />
                     <InputField name="address.houseNumber" fullWidth label={t('TRANSLATION:formFields.houseNumber')} />
                  </Box>
                  <Typography variant="h6">{t('CUSTOMERS:modal.new.paymentDetails')}</Typography>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                     <SelectField
                        fullWidth
                        noMinWidth
                        inputVariant="outlined"
                        name="deliveryType"
                        label={t('TRANSLATION:formFields.deliveryType')}
                        options={DeliveryTypes.map((type) => ({ value: type, name: t(`TRANSLATION:${type}`) }))}
                     />
                     <SelectField
                        fullWidth
                        noMinWidth
                        inputVariant="outlined"
                        name="paymentMethod"
                        label={t('TRANSLATION:formFields.paymentMethod')}
                        options={PaymentMethods.map((p) => ({ value: p, name: t(`TRANSLATION:${p}`) }))}
                     />
                  </Box>
                  <Typography>{t('CUSTOMERS:modal.new.deliveryAddresses')}</Typography>
                  {values.deliveryAddresses.map((_, index) => (
                     <>
                        <Divider flexItem>
                           <Typography>{t('CUSTOMERS:modal.new.deliveryAddress', { count: index + 1 })}</Typography>
                        </Divider>
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                           <Box flex={1} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Box sx={{ display: 'flex', gap: '8px' }}>
                                 <InputField
                                    name={`deliveryAddresses[${index}].zipCode`}
                                    fullWidth
                                    label={t('TRANSLATION:formFields.postCode')}
                                 />
                                 <InputField
                                    name={`deliveryAddresses[${index}].city`}
                                    fullWidth
                                    label={t('TRANSLATION:formFields.city')}
                                 />
                                 <InputField
                                    name={`deliveryAddresses[${index}].voivodeship`}
                                    fullWidth
                                    label={t('TRANSLATION:formFields.voivodeship')}
                                 />
                              </Box>
                              <Box sx={{ display: 'flex', gap: '8px' }}>
                                 <InputField
                                    name={`deliveryAddresses[${index}].county`}
                                    fullWidth
                                    label={t('TRANSLATION:formFields.county')}
                                 />
                                 <InputField
                                    name={`deliveryAddresses[${index}].street`}
                                    fullWidth
                                    label={t('TRANSLATION:formFields.street')}
                                 />
                                 <InputField
                                    name={`deliveryAddresses[${index}].houseNumber`}
                                    fullWidth
                                    label={t('TRANSLATION:formFields.houseNumber')}
                                 />
                              </Box>
                           </Box>
                           <IconButton
                              onClick={() => {
                                 const updated = [...values.deliveryAddresses]
                                 updated.splice(index, 1)
                                 setFieldValue('deliveryAddresses', updated)
                              }}
                              sx={{ '&:hover': { color: 'red' } }}
                           >
                              <Close />
                           </IconButton>
                        </Box>
                     </>
                  ))}
                  <IconButton
                     sx={{ alignSelf: 'center' }}
                     onClick={() => {
                        const newAddresses = [...values.deliveryAddresses]
                        if (values.deliveryAddresses.length > 0) {
                           newAddresses.push({
                              street: '',
                              houseNumber: '',
                              zipCode: '',
                              city: '',
                              voivodeship: '',
                              county: '',
                              country: 'Polska',
                           })
                        } else {
                           newAddresses.push({
                              street: values.address.street ?? '',
                              houseNumber: values.address.houseNumber ?? '',
                              zipCode: values.address.zipCode ?? '',
                              city: values.address.city ?? '',
                              voivodeship: values.address.voivodeship ?? '',
                              county: values.address.county ?? '',
                              country: 'Polska',
                           })
                        }
                        setFieldValue('deliveryAddresses', newAddresses)
                     }}
                  >
                     <Add />
                  </IconButton>
                  <Typography variant="h6">{t('CUSTOMERS:modal.new.customerData')}</Typography>
                  {values.contacts.map((contact, index) => (
                     <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                           <Box sx={{ display: 'flex', gap: '8px' }}>
                              <InputField
                                 name={`contacts[${index}]name`}
                                 fullWidth
                                 label={t('TRANSLATION:formFields.name')}
                              />
                              <InputField
                                 name={`contacts[${index}]surname`}
                                 fullWidth
                                 label={t('TRANSLATION:formFields.surname')}
                              />
                              <InputField name={`contacts[${index}]email`} fullWidth label="E-mail" />
                              <MuiPhone
                                 notRequired
                                 label={t('TRANSLATION:columnNames.phoneNumber')}
                                 variant="outlined"
                                 value={values.contacts[index].phoneNumber!}
                                 onChange={(value, country) => handlePhoneNumber(value, country, index)}
                              />
                           </Box>
                           <InputField
                              name={`contacts[${index}].comment`}
                              fullWidth
                              multiline
                              rows={2}
                              label={t('TRANSLATION:columnNames.description')}
                           />
                        </Box>
                        <IconButton
                           onClick={() => {
                              const updated = [...values.contacts]
                              updated.splice(index, 1)
                              setFieldValue('contacts', updated)
                           }}
                           sx={{ '&:hover': { color: 'red' } }}
                        >
                           <Close />
                        </IconButton>
                     </Box>
                  ))}
                  <IconButton
                     sx={{ alignSelf: 'center' }}
                     onClick={() => {
                        const newContacts = [...values.contacts]
                        newContacts.push({
                           name: '',
                           uuid: '',
                           surname: '',
                           email: '',
                           phoneNumber: '',
                           comment: '',
                        })
                        setFieldValue('contacts', newContacts)
                     }}
                  >
                     <Add />
                  </IconButton>
               </Box>
            </FormikProvider>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" disabled={isSubmitting} onClick={onClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton
               variant="contained"
               disabled={isSubmitting || phoneNumberError}
               onClick={() => handleSubmit()}
            >
               {t('TRANSLATION:save')}
            </ContainedButton>
         </DialogActions>
      </Box>
   )
}
